export const RUNNING_TIMER_LS_KEY = 'fixflow_running_timer';

export type RunningTimerPayload = {
  ticketId: string;
  ticketNumber: string;
  entryId: string;
  startedAt: string;
  repairName: string;
  /** Optional — scopes UI to the device/repair that owns the timer */
  repairId?: string;
};

const TIMER_EVENT = 'fixflow-running-timer-changed';

export function notifyRunningTimerChanged() {
  window.dispatchEvent(new Event(TIMER_EVENT));
}

export function readRunningTimer(): RunningTimerPayload | null {
  try {
    const raw = window.localStorage.getItem(RUNNING_TIMER_LS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as RunningTimerPayload;
    if (!v?.ticketId || !v.entryId || !v.startedAt || !v.ticketNumber) return null;
    return v;
  } catch {
    return null;
  }
}

export function writeRunningTimer(payload: RunningTimerPayload) {
  window.localStorage.setItem(RUNNING_TIMER_LS_KEY, JSON.stringify(payload));
  notifyRunningTimerChanged();
}

export function clearRunningTimer() {
  window.localStorage.removeItem(RUNNING_TIMER_LS_KEY);
  notifyRunningTimerChanged();
}

export function subscribeRunningTimer(cb: () => void) {
  const onStorage = (e: StorageEvent) => {
    if (e.key === RUNNING_TIMER_LS_KEY || e.key === null) cb();
  };
  const onLocal = () => cb();
  window.addEventListener('storage', onStorage);
  window.addEventListener(TIMER_EVENT, onLocal);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(TIMER_EVENT, onLocal);
  };
}

export function elapsedSecondsSince(iso: string): number {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 1000));
}

/** HH:MM:SS */
export function formatClockSeconds(total: number): string {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** e.g. 1h 23m or 45m */
export function formatDurationSeconds(total: number): string {
  if (total <= 0) return '0m';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}
